/**
 * Keeps one-time credentials out of analytics. `/auth/confirm-link` and
 * `/auth/confirm-email` render with `?token_hash=…` (a live recovery,
 * magic-link or sign-up token until the person presses the button), and PostHog
 * puts the whole URL on `$pageview` (`$current_url`, `$referrer`, `$initial_*`,
 * the session entry URL) and in the person properties. `before_send` runs this
 * on EVERY event, so nothing that can sign someone in or identify them leaves
 * the browser inside a URL.
 */

const SENSITIVE_PARAMS = new Set([
  'token_hash',
  'token',
  'code',
  'access_token',
  'refresh_token',
  'email',
])

/** The sign-up state `s` is only a credential-adjacent value on auth pages. */
const AUTH_ONLY_PARAMS = new Set(['s'])

/** Property names that hold a URL, a path or a referrer (PostHog's `$…` and ours). */
const URL_KEY = /(url|pathname|referrer)$/i

const BASE = 'https://scrub.invalid'

function isSensitive(name: string, onAuthPath: boolean): boolean {
  const key = name.toLowerCase()
  return SENSITIVE_PARAMS.has(key) || (onAuthPath && AUTH_ONLY_PARAMS.has(key))
}

/** The same URL without sensitive query or fragment parameters; other values pass through. */
export function scrubUrl(value: string): string {
  if (!value || (!value.includes('?') && !value.includes('#'))) return value
  let url: URL
  try {
    url = new URL(value, BASE)
  } catch {
    return value.split(/[?#]/)[0]
  }
  const onAuthPath = url.pathname.startsWith('/auth/')
  let changed = false
  for (const name of Array.from(new Set(url.searchParams.keys()))) {
    if (isSensitive(name, onAuthPath)) {
      url.searchParams.delete(name)
      changed = true
    }
  }
  if (url.hash) {
    const fragment = new URLSearchParams(url.hash.slice(1))
    let fragmentSensitive = false
    for (const name of fragment.keys()) if (isSensitive(name, onAuthPath)) fragmentSensitive = true
    if (fragmentSensitive) {
      url.hash = ''
      changed = true
    }
  }
  if (!changed) return value
  const absolute = /^[a-z][a-z0-9+.-]*:/i.test(value) || value.startsWith('//')
  const rest = `${url.pathname}${url.search}${url.hash}`
  return absolute ? `${url.origin}${rest}` : rest
}

function scrubRecord(record: unknown): unknown {
  if (!record || typeof record !== 'object' || Array.isArray(record)) return record
  const out: Record<string, unknown> = { ...(record as Record<string, unknown>) }
  for (const [key, val] of Object.entries(out)) {
    if (typeof val === 'string' && URL_KEY.test(key)) out[key] = scrubUrl(val)
  }
  return out
}

/** Applied to every captured event: its properties, `$set` and `$set_once`. */
export function scrubEventUrls<
  E extends { properties?: Record<string, unknown>; $set?: unknown; $set_once?: unknown },
>(event: E): E {
  const next: E = { ...event }
  if (event.properties) {
    const properties = scrubRecord(event.properties) as Record<string, unknown>
    next.properties = properties
    if (properties.$set) properties.$set = scrubRecord(properties.$set)
    if (properties.$set_once) properties.$set_once = scrubRecord(properties.$set_once)
  }
  if (event.$set) next.$set = scrubRecord(event.$set)
  if (event.$set_once) next.$set_once = scrubRecord(event.$set_once)
  return next
}
